import type { DeckSlide } from "./deck-parser"

import { parseDeck } from "./deck-parser"

// Minimal structural type for pptxgenjs (avoids importing its types in the signature).
type SlideInstance = {
  addText: (text: unknown, options: Record<string, unknown>) => void
}

type PptxLike = {
  addSlide: () => SlideInstance
  write: (opts: { outputType: string }) => Promise<unknown>
}

// Default factory — cast to our minimal type so the real pptxgenjs types don't bleed in.
const defaultLoadPptxgen: () => Promise<{ default: new () => PptxLike }> = () =>
  import("pptxgenjs") as Promise<{ default: new () => PptxLike }>

/** Builds a pptx blob from deck HTML. Injectable pptxgen factory for testing. */
export async function buildPptxBlob(
  html: string,
  loadPptxgen: () => Promise<{ default: new () => PptxLike }> = defaultLoadPptxgen
): Promise<Blob> {
  const slides: DeckSlide[] = parseDeck(html)
  const Pptxgen = (await loadPptxgen()).default
  const pptx = new Pptxgen()

  // If the deck is empty, add a placeholder slide to ensure a valid pptx
  if (slides.length === 0) {
    const s = pptx.addSlide()
    s.addText("(Tom presentation)", { x: 0.5, y: 0.3, w: 9, h: 1, fontSize: 28, bold: true })
  } else {
    for (const slide of slides) {
      const s = pptx.addSlide()
      if (slide.title) {
        s.addText(slide.title, { x: 0.5, y: 0.3, w: 9, h: 1, fontSize: 28, bold: true })
      }
      if (slide.bullets.length > 0) {
        s.addText(
          slide.bullets.map((text) => ({ text, options: { bullet: true } })),
          { x: 0.7, y: 1.5, w: 8.5, h: 4.5, fontSize: 18 }
        )
      }
    }
  }

  const result = await pptx.write({ outputType: "blob" })
  if (!(result instanceof Blob)) {
    throw new Error("pptx export did not return a Blob")
  }
  return result
}
